/**
 * Desktop-notification helpers (review finding L-27).
 *
 * Browsers ignore — and Chrome / Safari actively penalize (auto-deny, "quieter UI") —
 * `Notification.requestPermission()` calls that are not tied to a user gesture. The widget used to fire
 * it from the answer-completion path (a fetch/SSE callback), which is exactly that. The contract now:
 *   - NOTIFY only when permission is already `'granted'` (`canNotify`) — never request there;
 *   - REQUEST only from a click / keydown / submit handler (`requestNotificationsFromGesture`);
 *   - nothing in here is called from a network callback.
 * Structural `NotificationApiLike` so the logic is unit-testable without a browser.
 */
/**
 * Chromium's Android browsers (Chrome, Edge, Samsung Internet, WebView) have a `Notification` global but never let a page
 * construct one (`new Notification()` throws "Illegal constructor": only a service worker may show it there), so a page
 * there can never notify. Firefox for Android shows a page's notifications. Chromium alone reports `userAgentData`.
 */
export function pageNotificationsBlocked(nav) {
    if (!nav)
        return false;
    if (typeof nav.userAgentData?.platform === 'string' && nav.userAgentData.platform.toLowerCase() === 'android')
        return true;
    const agent = typeof nav.userAgent === 'string' ? nav.userAgent : '';
    return /\bAndroid\b/i.test(agent) && !/\bFirefox\//.test(agent);
}
/**
 * The global `Notification` API, or `null` where it does not exist (sandboxed iframe, WebView, SSR) or a page cannot use
 * it (Android, pageNotificationsBlocked): there nothing offers notifications that could never arrive.
 */
export function notificationApi() {
    try {
        if (typeof Notification === 'undefined')
            return null;
        if (typeof navigator !== 'undefined' && pageNotificationsBlocked(navigator))
            return null;
        return Notification;
    }
    catch {
        return null;
    }
}
/** True only when the user has ALREADY granted permission — the only state in which we may notify. */
export function canNotify(api) {
    return api?.permission === 'granted';
}
/** True when it is worth showing an opt-in control: supported and not yet decided. */
export function canOfferNotifications(api) {
    return api?.permission === 'default';
}
/**
 * Ask for permission. MUST be called from a user-gesture handler (button click, palette Enter, the
 * Send submit) — never from a completion callback. Resolves to the resulting permission, the current
 * permission when there was nothing to ask, or `'unsupported'` where the API is absent. Never throws.
 */
export async function requestNotificationsFromGesture(api) {
    if (!api)
        return 'unsupported';
    if (api.permission !== 'default')
        return api.permission;
    try {
        const result = await api.requestPermission();
        if (result === 'granted' || result === 'denied' || result === 'default')
            return result;
        // Callback-style API (returns undefined): the property reflects the decision once it settles.
        return api.permission ?? 'default';
    }
    catch {
        return api.permission ?? 'default';
    }
}
//# sourceMappingURL=notifications.js.map